import React, { useCallback, useEffect, useRef, useState } from 'react';

import GroupHeader from '../common/GroupHeader';

/**
 * The list beside the map (KPR-392): the same items the map shows, one row
 * each, and the keyboard's whole way through them — a map's pins cannot be
 * reached by Tab, so this list is what makes the map usable without a
 * pointer.
 *
 *   <ItemsMapList
 *     rows={[{ id, item, title, subtitle }]}      // the placed items, in the list's order
 *     unplacedRows={[{ id, item, title }]}        // the ones with no location
 *     selectedId                                  // the open item
 *     onOpen(item)                                // Enter, Space or a click
 *     onFocusItem(id | null)                      // the row the focus is on, for the map
 *     expandedItemId renderExpanded               // a phone: the open row grows into the detail
 *     label
 *   />
 *
 * One Tab stop (roving focus): the open row, else the last row focused,
 * else the first. ↑ and ↓ move the focus a row, Page Up and Page Down ten,
 * Home and End to either end — through the folded "No location" group too
 * once it is open — and Enter or Space opens the row's item. The keys it
 * handles stop here, so the page's own list keys (↑↓ move the selection,
 * Enter opens the item's page) never act twice; Esc, N and / pass through.
 */
const PAGE_STEP = 10;
const OWN_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp', 'Enter', ' ']);

function MapRow({ row, active, selected, onOpen, onFocusRow, expanded, renderExpanded, noLocation = false }) {
	return (
		<li className={`items-map__entry${selected ? ' items-map__entry--selected' : ''}`}>
			<button
				type="button"
				className={`items-map__row${selected ? ' items-map__row--selected' : ''}${noLocation ? ' items-map__row--unplaced' : ''}`}
				tabIndex={active ? 0 : -1}
				data-map-row={row.id}
				data-item-id={row.id}
				aria-current={selected ? 'true' : undefined}
				onClick={() => onOpen(row.item)}
				onFocus={() => onFocusRow(row.id)}
			>
				<span className="items-map__row-title">{row.title}</span>
				<span className="items-map__row-place">{noLocation ? 'No location' : row.subtitle}</span>
			</button>
			{expanded && typeof renderExpanded === 'function' ? (
				<div className="items-map__expanded">{renderExpanded(row.item)}</div>
			) : null}
		</li>
	);
}

export default function ItemsMapList({
	rows = [],
	unplacedRows = [],
	selectedId = null,
	onOpen,
	onFocusItem,
	expandedItemId = null,
	renderExpanded,
	label = 'Items on the map',
	unplacedOpenByDefault = false,
}) {
	const rootRef = useRef(null);
	const [focusedRowId, setFocusedRowId] = useState(null);
	const [unplacedOpen, setUnplacedOpen] = useState(unplacedOpenByDefault);
	// Opened by itself when nothing is placed; never folded by itself (the
	// first placed item arriving on a later page must not fold the group out
	// from under a reader working through it).
	useEffect(() => { if (unplacedOpenByDefault) setUnplacedOpen(true); }, [unplacedOpenByDefault]);

	// The open item may sit in the group (opened from the grid, then the map
	// chosen, or from this group): the group stays open while it does, or the
	// open row — and a phone's inline detail inside it — would be out of reach.
	const selectedUnplaced = Boolean(selectedId) && unplacedRows.some(row => row.id === selectedId);
	const groupOpen = unplacedOpen || selectedUnplaced;

	const reachable = groupOpen ? [...rows, ...unplacedRows] : rows;
	const has = id => Boolean(id) && reachable.some(row => row.id === id);
	const activeId = has(selectedId) ? selectedId : has(focusedRowId) ? focusedRowId : reachable[0]?.id || null;

	const onFocusRow = useCallback((id) => {
		setFocusedRowId(id);
		onFocusItem?.(id);
	}, [onFocusItem]);

	// Focus leaving the list altogether takes the ring off the map.
	const onBlur = useCallback((event) => {
		if (rootRef.current && event.relatedTarget && rootRef.current.contains(event.relatedTarget)) return;
		onFocusItem?.(null);
	}, [onFocusItem]);

	const onKeyDown = useCallback((event) => {
		if (!OWN_KEYS.has(event.key)) return;
		const from = event.target instanceof Element ? event.target.closest('[data-map-row]') : null;
		if (!from || !rootRef.current) return;
		// Enter and Space are the row button's own click; they only must not
		// also reach the page's list keys.
		event.stopPropagation();
		if (event.key === 'Enter' || event.key === ' ') return;
		event.preventDefault();
		const buttons = Array.from(rootRef.current.querySelectorAll('[data-map-row]'));
		const index = buttons.indexOf(from);
		if (index === -1) return;
		const last = buttons.length - 1;
		const next = {
			ArrowDown: Math.min(last, index + 1),
			ArrowUp: Math.max(0, index - 1),
			Home: 0,
			End: last,
			PageDown: Math.min(last, index + PAGE_STEP),
			PageUp: Math.max(0, index - PAGE_STEP),
		}[event.key];
		const target = buttons[next];
		if (target && target !== from) {
			target.focus();
			target.scrollIntoView?.({ block: 'nearest' });
		}
	}, []);

	// A pin clicked on the map opens its item: bring its row into view.
	useEffect(() => {
		// Item ids are hex; anything else is not looked for in a selector.
		if (!selectedId || !rootRef.current || !/^[\w-]+$/.test(selectedId)) return;
		const row = rootRef.current.querySelector(`[data-map-row="${selectedId}"]`);
		row?.scrollIntoView?.({ block: 'nearest' });
	}, [selectedId]);

	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions -- the keys belong to the row buttons inside; this only routes them
		<div className="items-map__lists" ref={rootRef} onKeyDown={onKeyDown} onBlur={onBlur}>
			{rows.length ? (
				<ul className="items-map__list" aria-label={label}>
					{rows.map(row => (
						<MapRow
							key={row.id}
							row={row}
							active={row.id === activeId}
							selected={row.id === selectedId}
							onOpen={onOpen}
							onFocusRow={onFocusRow}
							expanded={row.id === expandedItemId}
							renderExpanded={renderExpanded}
						/>
					))}
				</ul>
			) : null}
			{unplacedRows.length ? (
				<section className="items-map__unplaced">
					<GroupHeader
						label="No location"
						count={unplacedRows.length}
						countLabel="item"
						collapsible
						collapsed={!groupOpen}
						onToggle={() => setUnplacedOpen(!groupOpen)}
					/>
					{/* Folded, the rows are not mounted (standards §5, group headers). */}
					{groupOpen ? (
						<ul className="items-map__list" aria-label="Items with no location">
							{unplacedRows.map(row => (
								<MapRow
									key={row.id}
									row={row}
									active={row.id === activeId}
									selected={row.id === selectedId}
									onOpen={onOpen}
									onFocusRow={onFocusRow}
									expanded={row.id === expandedItemId}
									renderExpanded={renderExpanded}
									noLocation
								/>
							))}
						</ul>
					) : null}
				</section>
			) : null}
		</div>
	);
}
